import React, { useState } from "react";
import { ChatBubbleOutline } from "@mui/icons-material";
import { Comment } from "../types/blog";

interface CommentSectionProps {
  comments: Comment[];
  onAddComment: (content: string) => Promise<void>;
  onDeleteComment: (id: string) => Promise<void>;
}

export default function CommentSection({
  comments,
  onAddComment,
  onDeleteComment,
}: CommentSectionProps) {
  const [newComment, setNewComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim() && !isSubmitting) {
      try {
        setIsSubmitting(true);
        await onAddComment(newComment);
        setNewComment("");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="mt-8">
      <div className="flex items-center gap-2 mb-4">
        <ChatBubbleOutline
          sx={{ width: 20, height: 20 }}
          className="text-gray-600"
        />
        <h3 className="text-lg font-semibold">Comments ({comments.length})</h3>
      </div>

      <form onSubmit={handleSubmit} className="mb-6">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          placeholder="Add a comment..."
          rows={3}
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? "Posting..." : "Post Comment"}
        </button>
      </form>

      <div className="space-y-4">
        {comments.map((comment) => (
          <div key={comment.id} className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-medium">{comment.author}</span>
              <span className="text-gray-500 text-sm">
                {new Date(comment.date).toLocaleDateString()}
              </span>
              <button
                onClick={() => onDeleteComment(comment.id)}
                className="ml-auto text-red-500 hover:text-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
            <p className="text-gray-700">{comment.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
